import { useState, useEffect, useContext } from 'react';
import { 
  Ticket, 
  LayoutDashboard, 
  MapPin, 
  CalendarDays, 
  HelpCircle, 
  ShieldCheck, 
  Smartphone, 
  Briefcase, 
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { voucherService } from '../../services/vouchers/voucherService';
import { appBookingService } from '../../services/appBookings/appBookingService';
import { supportService } from '../../services/support/supportService';
import { useSubscription } from '../../context/subscription/SubscriptionContext';
import DashboardContext from '../../context/dashboard/DashboardContext';

const Sidebar = ({ activeTab, setActiveTab }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    
    // Get live dashboard stats for dynamic counters
    const dashboardContext = useContext(DashboardContext);
    const stats = dashboardContext?.stats;

    const [counts, setCounts] = useState({
        vouchers: stats?.totalVouchers ?? 0,
        bookings: stats?.totalBookings ?? 0
    });

    const toggleCollapse = () => {
        const nextState = !isCollapsed;
        setIsCollapsed(nextState);
        localStorage.setItem('sidebar_collapsed', String(nextState));
        window.dispatchEvent(new Event('sidebar_state_changed'));
    };

    // Keep counts in sync with DashboardContext stats
    useEffect(() => {
        if (stats) {
            setCounts((prev) => ({
                vouchers: stats.totalVouchers !== undefined ? stats.totalVouchers : prev.vouchers,
                bookings: stats.totalBookings !== undefined ? stats.totalBookings : prev.bookings
            }));
        }
    }, [stats]);

    // Live background fetch for Vouchers & Bookings count
    useEffect(() => {
        const fetchLiveCounts = async () => {
            try {
                const [vouchersRes, bookingsRes] = await Promise.all([
                    voucherService.getVouchers().catch(() => null),
                    appBookingService.getAppBookings().catch(() => null)
                ]);

                setCounts((prev) => {
                    const newVouchers = Array.isArray(vouchersRes) 
                        ? vouchersRes.length 
                        : (Array.isArray(vouchersRes?.vouchers) ? vouchersRes.vouchers.length : (Array.isArray(vouchersRes?.data) ? vouchersRes.data.length : prev.vouchers));

                    const bookingsData = bookingsRes?.data || bookingsRes;
                    const newBookings = Array.isArray(bookingsData) ? bookingsData.length : prev.bookings;

                    return {
                        vouchers: newVouchers,
                        bookings: newBookings
                    };
                });
            } catch (e) {
                // silent
            }
        };

        fetchLiveCounts();
        const interval = setInterval(fetchLiveCounts, 20000);
        return () => clearInterval(interval);
    }, [location.pathname]);

    useEffect(() => {
        const fetchUnread = async () => {
            try {
                const res = await supportService.getAllChats();
                if (res?.success) {
                    setUnreadChatCount(res?.data?.totalUnread || 0);
                }
            } catch (e) {
                // silent
            }
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, 15000);
        return () => clearInterval(interval);
    }, [location.pathname]);

    const { subscription } = useSubscription();
    const isEnterprisePlan = subscription && 
        subscription.status === 'active' && 
        subscription.plan_id && 
        (subscription.plan_id.slug === 'enterprise' || subscription.plan_id.name?.toLowerCase().includes('enterprise'));

    const subUserRole = localStorage.getItem('sub_user_role');
    const isBookingsRole = subUserRole === 'bookings';
    const isOperationsRole = subUserRole === 'operations';
    const canManageRoles = isEnterprisePlan && subUserRole !== 'bookings' && subUserRole !== 'operations';

    const voucherCount = counts.vouchers;
    const bookingCount = counts.bookings;

    // Grouped Sidebar Sections
    const navigationSections = [
        {
            title: 'ON-TRRIP',
            items: [
                { id: 'home', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
                ...(!isOperationsRole ? [{ 
                    id: 'voucher', 
                    label: 'Vouchers', 
                    icon: Ticket, 
                    path: '/voucher',
                    count: voucherCount
                }] : []),
                { 
                    id: 'bookings', 
                    label: 'App bookings', 
                    icon: CalendarDays, 
                    path: '/bookings',
                    count: bookingCount
                },
                ...(!isBookingsRole ? [{
                    id: 'tracking',
                    label: 'Tracking tools',
                    icon: MapPin,
                    path: '/tracking'
                }] : [])
            ]
        },
        {
            title: 'GROW',
            items: [
                ...(!isBookingsRole ? [{ 
                    id: 'post-connect', 
                    label: 'Post Trrip Connect', 
                    icon: Smartphone, 
                    path: '/post-connect'
                }] : []),
                ...(!isBookingsRole ? [{ 
                    id: 'partners', 
                    label: 'Partners', 
                    icon: Briefcase, 
                    path: '/partners' 
                }] : [])
                /* Temporarily locked for all plans & hidden from sidebar - kept for future release
                , ...(!isBookingsRole ? [{ 
                    id: 'your-itinerary', 
                    label: 'Itinerary', 
                    icon: Compass, 
                    path: '/your-itinerary' 
                }] : [])
                */
            ]
        },
        {
            title: 'MANAGE',
            items: [
                ...(canManageRoles ? [{ 
                    id: 'role-management', 
                    label: 'Team & roles', 
                    icon: ShieldCheck, 
                    path: '/role-management' 
                }] : []),
                { 
                    id: 'feed-back', 
                    label: 'Traveller feedback', 
                    icon: MessageSquare, 
                    path: '/feed-back' 
                },
                { 
                    id: 'support', 
                    label: 'Support', 
                    icon: HelpCircle, 
                    path: '/support',
                    unreadCount: unreadChatCount
                }
            ]
        }
    ];

    const isPathActive = (item) => {
        if (!item.path) return false;
        if (item.path === '/dashboard') {
            return location.pathname === '/' || location.pathname === '/dashboard';
        }
        if (item.path === '/voucher') {
            return location.pathname.startsWith('/voucher') || location.pathname === '/create-voucher';
        }
        if (item.path === '/tracking') {
            return location.pathname.startsWith('/tracking');
        }
        if (item.path === '/your-itinerary') {
            return location.pathname === '/your-itinerary' || location.pathname === '/predefined-itinerary';
        }
        if (item.path === '/feed-back') {
            return location.pathname === '/feed-back' || location.pathname === '/feedback';
        }
        return location.pathname.startsWith(item.path);
    };

    const formatMiniLabel = (item) => {
        const id = item.id;
        if (id === 'home') return <span>Home</span>;
        if (id === 'voucher') return <span>Vouchers</span>;
        if (id === 'bookings') return <><span className="block">App</span><span className="block">bookings</span></>;
        if (id === 'tracking') return <><span className="block">Tracking</span><span className="block">tools</span></>;
        if (id === 'post-connect') return <><span className="block">Post Trrip</span><span className="block">Connect</span></>;
        if (id === 'partners') return <span>Partners</span>;
        if (id === 'your-itinerary') return <span>Itinerary</span>;
        if (id === 'role-management') return <><span className="block">Team &</span><span className="block">roles</span></>;
        if (id === 'feed-back') return <><span className="block">Traveller</span><span className="block">feedback</span></>;
        if (id === 'support') return <span>Support</span>;
        return item.label;
    };

    return (
        <aside className={`hidden min-[950px]:flex ${isCollapsed ? 'w-[92px]' : 'w-64'} bg-white border-r border-gray-100 flex-col h-full flex-shrink-0 relative z-30 overflow-visible select-none transition-all duration-300`}>
            
            {/* Toggle Button when Collapsed - Placed outside sidebar edge with white background */}
            {isCollapsed && (
                <button
                    type="button"
                    onClick={toggleCollapse}
                    title="Expand sidebar"
                    className="absolute left-full top-3 -ml-px z-50 bg-white border-y border-r border-gray-100 rounded-r-lg p-1.5 text-slate-400 hover:text-[#067978] shadow-xs flex items-center justify-center transition-all cursor-pointer hover:bg-slate-50"
                >
                    <PanelLeftOpen size={16} />
                </button>
            )}

            {/* Navigation Menu */}
            <nav className="flex-1 p-2 space-y-2.5 overflow-y-auto no-scrollbar">
                {navigationSections.map((section, idx) => (
                    <div key={section.title || idx} className="space-y-1">
                        {/* Section Header when Open */}
                        {!isCollapsed && (
                            <div className="flex items-center justify-between px-3 pt-2 pb-1">
                                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                                    {section.title}
                                </span>
                                {idx === 0 && (
                                    <button
                                        type="button"
                                        onClick={toggleCollapse}
                                        title="Collapse sidebar"
                                        className="p-1 rounded-lg text-slate-400 hover:text-[#067978] hover:bg-slate-50 transition-all cursor-pointer"
                                    >
                                        <PanelLeftClose size={16} />
                                    </button>
                                )}
                            </div>
                        )}

                        {isCollapsed && idx > 0 && (
                            <div className="w-full h-px bg-slate-100 my-1.5" />
                        )}

                        {/* Section Navigation Items */}
                        <div className="space-y-1">
                            {section.items.map((item) => {
                                const Icon = item.icon;



                                const isActive = isPathActive(item);

                                if (isCollapsed) {
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            data-tour={item.id === 'voucher' ? 'nav-vouchers' : `nav-${item.id}`}
                                            onClick={() => {
                                                if (item.path) navigate(item.path);
                                                else setActiveTab?.(item.id);
                                            }}
                                            title={item.label}
                                            className={`flex flex-col items-center justify-center w-full py-2.5 px-1 rounded-xl text-[11.5px] font-medium transition-all duration-200 group relative ${
                                                isActive
                                                    ? 'bg-[#067978] text-white shadow-xs font-semibold'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-[#067978]'
                                            }`}
                                        >
                                            <Icon size={19} className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-[#067978]'}`} />
                                            <div className="text-center mt-1 leading-[1.15]">
                                                {formatMiniLabel(item)}
                                            </div>

                                            {/* Badge or Count Indicator */}
                                            {item.unreadCount > 0 && (
                                                <span className="absolute top-1 right-1.5 w-2 h-2 rounded-full bg-[#067978] ring-2 ring-white" />
                                            )}
                                        </button>
                                    );
                                }

                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        data-tour={item.id === 'voucher' ? 'nav-vouchers' : `nav-${item.id}`}
                                        onClick={() => {
                                            if (item.path) navigate(item.path);
                                            else setActiveTab?.(item.id);
                                        }}
                                        className={`flex items-center w-full gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-normal transition-all duration-200 group ${
                                            isActive
                                                ? 'bg-[#067978] text-white shadow-xs font-medium'
                                                : 'text-slate-600 hover:bg-slate-50 hover:text-[#067978]'
                                        }`}
                                    >
                                        <Icon size={18} className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-[#067978]'} transition-colors`} />
                                        <span>{item.label}</span>

                                        {/* Count Badge (e.g. Vouchers, Bookings) */}
                                        {item.count !== undefined && (
                                            <span className={`ml-auto text-xs font-normal ${
                                                isActive ? 'text-white/90' : 'text-slate-400'
                                            }`}>
                                                {item.count}
                                            </span>
                                        )}

                                        {/* Pro Badge */}
                                        {item.badge && (
                                            <span className={`ml-auto text-[10px] font-normal px-2 py-0.5 rounded-md tracking-tight transition-all ${
                                                isActive 
                                                    ? 'bg-white text-[#067978]' 
                                                    : 'bg-[#E6F4F1] text-[#067978]'
                                            }`}>
                                                {item.badge}
                                            </span>
                                        )}

                                        {/* Unread Chat Badge for Support */}
                                        {item.unreadCount > 0 && (
                                            <span className={`ml-auto w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-normal tracking-tight transition-all duration-200 ${
                                                isActive
                                                    ? 'bg-white text-[#067978] shadow-xs'
                                                    : 'bg-[#067978] text-white shadow-xs'
                                            }`}>
                                                {item.unreadCount > 9 ? '9+' : item.unreadCount}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>
        </aside>
    );
};

export default Sidebar;

